import React, { useCallback, useEffect, useState } from 'react';
import { ExternalLink, LayoutTemplate, Loader2, Pencil, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { builderUrl, cmsApi, cmsPageApi, WEBSITE_BASE_URL, type CmsCustomPage } from '../../../../lib/api/cms';
import { Button } from '../../../../components/ui/button';
import { Input } from '../../../../components/ui/input';
import { Label } from '../../../../components/ui/label';
import { Badge } from '../../../../components/ui/badge';
import { Card, CardContent } from '../../../../components/ui/card';
import LoeschenMitGrundDialog from '../../../../components/ui/loeschen-mit-grund-dialog';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../../../components/ui/dialog';
import CmsContentEditor from '../../../../components/cms/CmsContentEditor';
// Spiegelt das Backend, damit der angelegte Pfad dem entspricht, was serverseitig gespeichert wird.
import { slugifyPath } from '../../../../lib/slugify';

const pagePath = (slug: string) => slug.replace(/^page\//, '');

const CmsPagesPage: React.FC = () => {
  const [pages, setPages] = useState<CmsCustomPage[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<CmsCustomPage | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newSlug, setNewSlug] = useState('');
  const [creating, setCreating] = useState(false);
  const [reservedPaths, setReservedPaths] = useState<string[]>([]);
  const [zuLoeschen, setZuLoeschen] = useState<CmsCustomPage | null>(null);

  useEffect(() => {
    cmsApi.getReservedPaths().then(setReservedPaths).catch(() => { /* optional */ });
  }, []);

  const cleanPath = slugifyPath(newSlug || newTitle);
  const firstSegment = cleanPath.split('/')[0];
  const isReserved = !!firstSegment && reservedPaths.includes(firstSegment);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setPages(await cmsPageApi.list());
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Seiten konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  // Das Fenster sofort öffnen, das Ziel erst nach dem Token setzen — sonst
  // blockt der Browser das Popup, weil es nicht mehr direkt am Klick hängt.
  const imBuilderOeffnen = async (p: CmsCustomPage) => {
    const fenster = window.open('', '_blank');
    try {
      const { token } = await cmsApi.getBuilderUebergabe();
      if (fenster) fenster.location.href = builderUrl(token, p.id);
      else window.location.href = builderUrl(token, p.id);
    } catch (e) {
      fenster?.close();
      toast.error(e instanceof Error ? e.message : 'Der Builder konnte nicht geöffnet werden');
    }
  };

  const create = async () => {
    const slug = (newSlug || newTitle).trim();
    if (!slug || !newTitle.trim()) {
      toast.error('Titel und Pfad sind erforderlich');
      return;
    }
    if (isReserved) {
      toast.error(`Der Pfad „/${firstSegment}" ist reserviert.`);
      return;
    }
    setCreating(true);
    try {
      const page = await cmsPageApi.create(slug, newTitle.trim());
      setCreateOpen(false);
      setNewTitle('');
      setNewSlug('');
      await load();
      setEditing(page);
      toast.success('Seite erstellt');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Seite konnte nicht erstellt werden');
    } finally {
      setCreating(false);
    }
  };

  const remove = (p: CmsCustomPage) => setZuLoeschen(p);

  const loeschen = async (p: CmsCustomPage, grund: string) => {
    await cmsPageApi.remove(p.id, grund);
    setPages((prev) => prev.filter((x) => x.id !== p.id));
    setZuLoeschen(null);
    toast.success('Seite gelöscht');
  };

  return (
    <div className="mx-auto max-w-5xl space-y-6 p-4 sm:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Eigene Seiten</h1>
          <p className="text-sm text-muted-foreground">
            Eigene Seiten aus Blöcken zusammenstellen – erreichbar unter <code>/&lt;pfad&gt;</code> (Root, optional mit Unterpfad).
          </p>
        </div>
        <Button type="button" onClick={() => setCreateOpen(true)}>
          <Plus className="mr-2 h-4 w-4" /> Neue Seite
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16 text-muted-foreground">
          <Loader2 className="h-6 w-6 animate-spin" />
        </div>
      ) : pages.length === 0 ? (
        <Card>
          <CardContent className="py-10 text-center text-sm text-muted-foreground">
            Noch keine eigenen Seiten. Lege die erste an.
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="divide-y p-0">
            {pages.map((p) => (
              <div key={p.id} className="flex flex-wrap items-center gap-4 px-4 py-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="truncate font-medium">{p.title}</span>
                    <Badge variant={p.status === 'published' ? 'default' : 'secondary'} size="sm">
                      {p.status === 'published' ? 'Veröffentlicht' : 'Entwurf'}
                    </Badge>
                    {p.status === 'published' && p.changes_pending ? (
                      <Badge variant="outline" size="sm" className="border-amber-300 text-amber-700">
                        Änderungen offen
                      </Badge>
                    ) : null}
                  </div>
                  <a
                    href={`${WEBSITE_BASE_URL}/${pagePath(p.slug)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-primary"
                  >
                    /{pagePath(p.slug)} <ExternalLink className="h-3 w-3" />
                  </a>
                </div>
                <Button type="button" variant="outline" size="sm" onClick={() => setEditing(p)}>
                  <Pencil className="mr-2 h-4 w-4" /> Bearbeiten
                </Button>
                <Button type="button" variant="outline" size="sm" onClick={() => void imBuilderOeffnen(p)}>
                  <LayoutTemplate className="mr-2 h-4 w-4" /> Visuell gestalten
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="text-destructive hover:text-destructive"
                  onClick={() => remove(p)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Create dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Neue Seite</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Titel</Label>
              <Input value={newTitle} onChange={(e) => setNewTitle(e.target.value)} placeholder="z. B. Musikschule gründen" />
            </div>
            <div className="space-y-1.5">
              <Label>Pfad (URL)</Label>
              <Input
                value={newSlug}
                onChange={(e) => setNewSlug(e.target.value)}
                placeholder="musikschule-gruenden oder info/preise (leer = aus Titel)"
              />
              {isReserved ? (
                <p className="text-xs text-destructive">
                  Der Pfad „/{firstSegment}" ist reserviert (Website-Route). Bitte einen anderen wählen.
                </p>
              ) : (
                <p className="text-xs text-muted-foreground">
                  Erreichbar unter /{cleanPath || 'pfad'} — Unterpfad mit „/" möglich.
                </p>
              )}
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => setCreateOpen(false)}>
              Abbrechen
            </Button>
            <Button type="button" loading={creating} onClick={create} disabled={isReserved || !cleanPath}>
              Erstellen
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Editor dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => { if (!o) { setEditing(null); void load(); } }}>
        <DialogContent size="full" className="h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? `Seite: ${editing.title}` : ''}</DialogTitle>
          </DialogHeader>
          {editing && (
            <CmsContentEditor
              key={editing.id}
              slug={editing.slug}
              title={editing.title}
              description={`/${pagePath(editing.slug)}`}
              previewPath={`/${pagePath(editing.slug)}`}
            />
          )}
        </DialogContent>
      </Dialog>
      <LoeschenMitGrundDialog
        offen={zuLoeschen !== null}
        titel={`Seite „${zuLoeschen?.title ?? ''}“ löschen?`}
        onAbbrechen={() => setZuLoeschen(null)}
        onLoeschen={async (grund) => {
          if (zuLoeschen) await loeschen(zuLoeschen, grund);
        }}
      />
    </div>
  );
};

export default CmsPagesPage;
